import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { httpClient } from '../../../shared/api/httpClient'
import { useAuth } from '../../../shared/context/AuthContext'
import { useAstreaEnvironment } from '../../../shared/hooks/useAstreaEnvironment'

/**
 * Port of `app/discountCoupon/discountCouponsService.js` (AngularJS
 * `DiscountCouponsService`) + the fetch/action logic of
 * `app/discountCoupon/discountCouponsController.js` (`discountCouponsController`,
 * the controller for `discountCoupons.html`, the "Cupons de desconto"
 * listing screen — legacy state `workspace.discountCoupons`,
 * `/workspace/discount-coupons`) to TanStack Query hooks, following the same
 * one-hook-per-legacy-call shape as `useMigrationQuery.js`/
 * `useCustomerQuery.js` (plain `useMutation`s: the legacy controller is
 * entirely imperative/button-driven with no natural query key, same
 * reasoning documented in those hook files).
 *
 * Task 031 scope only: the main listing/search screen and its own actions
 * (search, expire coupon, remove automatic coupons). The four "add coupon"
 * screens (`discountCouponsAdd`, `automaticDiscountCouponsAdd`,
 * `ambassadorDiscountCouponsAdd`, `comboDiscountCouponsAdd`) and their
 * services (`save`, `saveAutomaticCoupons`, `saveAmbassadorCoupons`) are
 * explicitly OUT of scope here — tasks 032-034 own them.
 *
 * All requests hit the *external* Astrea backend
 * (`AuthService.getUrlAstreaPath()` in the legacy service), same
 * cross-origin pattern as `useMigrationQuery`/`useCustomerQuery` — confirmed
 * by reading `discountCouponsService.js`: every method there uses
 * `discountCouponsService.getUrlAtrea()` (a typo'd wrapper for
 * `AuthService.getUrlAstreaPath()`, kept internal to the legacy service, not
 * reproduced here — this file calls `getUrlAstreaPath(getUrlAstrea())`
 * directly like every other ported module) except `saveStatusLog`, which
 * posts to THIS repo's `server_app` (`POST /statusLogs/create`,
 * `server_app/routes/statusLogsRouter.js`), exactly like
 * `CustomerService.saveStatusLog` in `useCustomerQuery.js`.
 */

/**
 * Port of `discountCouponsService.setParams(tenantId, code)`:
 *
 *   discountCouponsService.setParams = function(tenantId, code){
 *     var params = "";
 *     if (tenantId != null && tenantId != undefined && tenantId != "") {
 *       params = 'tenantId=' + tenantId;
 *     }
 *     if (code != null && code != undefined && code != "") {
 *       if (params.length == 0) params = 'code=' + code;
 *       else params = '&code=' + code;
 *     }
 *     return params;
 *   };
 *
 * Reproduced faithfully, including a pre-existing bug: when BOTH `tenantId`
 * and `code` are non-empty, `params` is first set to `'tenantId=' + tenantId`,
 * then the `code` branch checks `params.length == 0` (false, since
 * `tenantId` already populated it) and takes the `else` branch — which
 * *reassigns* `params = '&code=' + code` instead of appending to the
 * existing value. The net effect: `tenantId` is silently dropped from the
 * request whenever both filters are filled in, and the final query string is
 * `?&code=<code>` (a stray leading `&` right after `?`, no `tenantId=`
 * anywhere). Confirmed by reading `discountCouponsService.setParams`
 * character-for-character; kept 1:1 (not "fixed") per this task's
 * instructions to document rather than correct legacy behavior — see the
 * "both filters" test case in `useDiscountCouponQuery.test.jsx`.
 */
function buildDiscountCouponParams(tenantId, code) {
  let params = ''

  if (tenantId !== null && tenantId !== undefined && tenantId !== '') {
    params = `tenantId=${tenantId}`
  }

  if (code !== null && code !== undefined && code !== '') {
    if (params.length === 0) {
      params = `code=${code}`
    } else {
      params = `&code=${code}`
    }
  }

  return params
}

/**
 * Port of `DiscountCouponsService.getDiscountCoupons(tenantId, code)`:
 *
 *   discountCouponsService.getDiscountCoupons = function(tenantId, code) {
 *     var params = discountCouponsService.setParams(tenantId, code);
 *     var urlAstrea = discountCouponsService.getUrlAtrea();
 *     return $http({
 *       method: 'GET',
 *       url: urlAstrea + '/api/v2/discount-coupon?' + params,
 *       headers: {'Content-Type': 'application/x-www-form-urlencoded'}
 *     }).then(function(data) {
 *       discountCouponsService.discountCoupons = data;
 *       for (var i = 0; i < discountCouponsService.discountCoupons.length; i++) {
 *         discountCouponsService.discountCoupons[i].fromDate = new Date(...);
 *         discountCouponsService.discountCoupons[i].createAt = new Date(...);
 *         discountCouponsService.discountCoupons[i].dueDate = new Date(...);
 *       }
 *       return data;
 *     }).catch(function() {
 *       return new Error("Erro ao carregar os cupons de desconto");
 *     });
 *   };
 *
 * Two legacy quirks reproduced as-is (documented, not fixed):
 * - The `for` loop assigns `.fromDate`/`.createAt`/`.dueDate` onto
 *   `discountCouponsService.discountCoupons` (the *service's* cached array,
 *   built from the raw `data` — the `$http` response object, not
 *   `data.data`), so in practice that loop iterates over an object with no
 *   numeric `.length`, never actually running, and the field values it
 *   claims to normalize into `Date` objects are dead code — the controller
 *   itself never reads `discountCouponsService.discountCoupons`, only
 *   `resp.data` directly (`$scope.discountCoupons = resp.data`). So dates
 *   arrive at the page as whatever `resp.data[i].fromDate`/`dueDate` already
 *   are (ISO strings from the API), not pre-converted `Date` objects; the
 *   page component formats them itself (see `DiscountCouponsListPage.jsx`).
 * - The `.catch` handler returns a resolved `Error` object (not a rejected
 *   promise) — `discountCouponsController.getDiscountCoupons`'s error
 *   callback is therefore *never* invoked by this specific service method on
 *   HTTP failure (the promise chain always resolves), so the controller's
 *   `Materialize.toast('Ocorreu um erro: '+error.statusText, ...)` for this
 *   call is dead code in the legacy app. Reproduced as a `useMutation` that
 *   never rejects on transport failure either — `DiscountCouponsListPage.jsx`
 *   accordingly renders whatever this resolves with, matching the legacy
 *   controller's `if (resp.data.id != null)` / `else` branching on the
 *   resolved value.
 *
 * `resp.data.id != null` in the controller means "the API returned a single
 * coupon object" (searching by `code` returns one object, not an array) vs.
 * "the API returned an array" (searching by `tenantId`, or no filters) —
 * ported as `normalizeDiscountCoupons` below, called by the page.
 */
export function useDiscountCouponSearchMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async ({ tenantId, code }) => {
      const params = buildDiscountCouponParams(tenantId, code)

      try {
        const response = await httpClient.get(`${baseUrl}/api/v2/discount-coupon?${params}`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        })
        return response.data
      } catch {
        // Legacy `.catch` resolves with an `Error` instead of rejecting —
        // see doc-comment above. Reproduced 1:1: the mutation "succeeds"
        // with this value rather than going through `onError`.
        return new Error('Erro ao carregar os cupons de desconto')
      }
    },
  })
}

/**
 * Port of `discountCouponsController.getDiscountCoupons`'s response
 * branching:
 *
 *   if (resp.data.id != null) {
 *     $scope.discountCoupons = [];
 *     $scope.discountCoupons.push(resp.data);
 *   } else {
 *     $scope.discountCoupons = resp.data;
 *   }
 *
 * Exposed as a standalone helper (not folded into the mutation above) so
 * `DiscountCouponsListPage.jsx` can apply it to the mutation's resolved
 * value, including the resolved-`Error` case from the `.catch` above (an
 * `Error` instance has no `.id`, so it falls into the `else` branch just
 * like the legacy code would — `$scope.discountCoupons = <Error instance>`,
 * which then fails `ng-repeat`/`.length` checks silently in the legacy
 * template; reproduced here by treating a non-array as an empty list, since
 * MUI `Table`'s `.map` needs an array — the *outcome* users saw, an empty
 * table with no coupons and no visible error, matches).
 */
export function normalizeDiscountCoupons(data) {
  if (data && typeof data === 'object' && data.id != null) {
    return [data]
  }

  return Array.isArray(data) ? data : []
}

/**
 * Port of `DiscountCouponsService.expireCoupon(couponCode)`:
 *
 *   discountCouponsService.expireCoupon = function (couponCode) {
 *     var urlAstrea = discountCouponsService.getUrlAtrea();
 *     return $http({
 *       method: "POST",
 *       url: urlAstrea + "/api/v2/discount-coupon/expire/" + couponCode,
 *       headers: {"Content-Type": "application/json"}
 *     }).then(function (data) { return data; });
 *   };
 *
 * Backs the details modal's "EXPIRAR AGORA" button
 * (`discountCouponsController.expireNow`), which on success sets
 * `selectedDiscountCoupon.expired = true` and `dueDate` to yesterday
 * locally (no re-fetch) — ported the same way in
 * `DiscountCouponsListPage.jsx`.
 */
export function useExpireCouponMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (couponCode) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/discount-coupon/expire/${encodeURIComponent(couponCode)}`,
        {},
        { headers: { 'Content-Type': 'application/json' } },
      )
      return response.data
    },
  })
}

/** Port of `DiscountCouponsService.saveStatusLog(log)`: `POST /statusLogs/create`. */
async function saveStatusLog(log) {
  return httpClient.post('/statusLogs/create', log)
}

/**
 * Port of `buildLogNamespace(userViewerLogin)` + `buildLogPayload()`:
 *
 *   var buildLogNamespace = function (userViewerLogin) {
 *     const [namespace] = userViewerLogin.split("@");
 *     return namespace;
 *   };
 *   var buildLogPayload = function () {
 *     return {
 *       who: $scope.userViewerLogin,
 *       namespace: buildLogNamespace($scope.userViewerLogin),
 *       oldStatus: "BLANK",
 *       newStatus: `Automatic coupons cleaned at: ${new Date()}`
 *     };
 *   };
 *
 * `userViewerLogin` in the legacy controller comes from
 * `AuthService.getUser().then(user => $scope.userViewerLogin = user.login)`
 * — note this reads the `.login` field off the `/users/getUser` response,
 * distinct from `AuthService.getUserLogin()` (the session's login, read off
 * the `userLogin` cookie/session field elsewhere in this app). Both
 * ultimately hold the same logged-in user's email in practice, so this is
 * ported using `useAuth().getUserLogin()` (the session value already
 * available everywhere else, e.g. `useCustomerQuery.js`'s
 * `createLogForContract`) rather than adding a second `/users/getUser`
 * round-trip only to re-read the same value under a different field name.
 */
function buildAutomaticCouponsLogPayload(userLogin, profileTypes) {
  const [namespace] = (userLogin || '').split('@')
  const profileTypesLabel =
    Array.isArray(profileTypes) && profileTypes.length > 0 ? profileTypes.join(',') : 'TODOS'

  return {
    who: userLogin,
    namespace,
    oldStatus: 'BLANK',
    newStatus: `Automatic coupons cleaned (profileTypes: ${profileTypesLabel}) at: ${new Date()}`,
  }
}

/**
 * Port of `DiscountCouponsService.removeAutomaticCoupons()`:
 *
 *   discountCouponsService.removeAutomaticCoupons = function() {
 *     var urlAstrea = discountCouponsService.getUrlAtrea();
 *     return $http({
 *       method: 'DELETE',
 *       url: urlAstrea + "/api/v2/dev/discount-coupon/automatic-clean-up",
 *       headers: {'Content-Type': 'application/json'}
 *     }).then(function (data) { return data; });
 *   };
 *
 * Plus `discountCouponsController.removeAutomaticCoupons`'s follow-up
 * `DiscountCouponsService.saveStatusLog(logPayload)` call — fired
 * unconditionally right after the delete request is *issued* (not after it
 * resolves; the legacy code calls `.then(...)` on the delete promise and
 * `saveStatusLog` right after, not chained inside that `.then`), so both
 * requests fire back-to-back rather than the log waiting on the delete's
 * response. Reproduced the same way: both awaited in sequence inside this
 * one `mutationFn` (network order preserved), but the log's own
 * success/failure isn't surfaced to the caller either, exactly like legacy
 * (`DiscountCouponsService.saveStatusLog(logPayload)` result is discarded,
 * no `.then`/`.catch` attached at the call site).
 */
export function useRemoveAutomaticCouponsMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (profileTypes) => {
      const response = await httpClient.post('/discount-coupons/automatic-clean-up', {
        profileTypes,
      })

      await saveStatusLog(buildAutomaticCouponsLogPayload(getUserLogin(), profileTypes))

      return response.data
    },
  })
}

/**
 * Port of `DiscountCouponsService.save(discountCoupon)`:
 *
 *   discountCouponsService.save = function(discountCoupon) {
 *     var urlAstrea = discountCouponsService.getUrlAtrea();
 *     return $http({
 *       method: 'POST',
 *       url: urlAstrea + '/api/v2/discount-coupon/',
 *       headers: {'Content-Type': 'application/json'},
 *       data: discountCoupon
 *     }).then(function(data, status, headers, config) {
 *       if (status == 200) { discountCouponsService.discountCoupons.push(data) }
 *       return data;
 *     });
 *   };
 *
 * Backs `discountCouponsAddController.js`'s standard/manual "add coupon"
 * form (task 032, `DiscountCouponAddPage.jsx`) — the one endpoint shared by
 * all four "add coupon" variants (regular/automatic/ambassador/combo), each
 * hitting a *different* URL (`saveAutomaticCoupons` ->
 * `/api/v2/dev/discount-coupon/automatic`, `saveAmbassadorCoupons` ->
 * `/api/v2/dev/discount-coupon/automatic-marketing-partner`), so this
 * mutation is deliberately named/scoped to the plain `save` call only —
 * tasks 033/034 add their own sibling mutations here for their own
 * endpoints rather than this one being reused for all four.
 *
 * The `status == 200` guard pushing onto `discountCouponsService.
 * discountCoupons` (the service's own in-memory cache, never read by
 * `discountCouponsController` — see the identical dead-cache note on
 * `getDiscountCoupons` above) is legacy dead code with no observable effect
 * on any screen; not reproduced here for the same reason it isn't
 * reproduced for the other cache-touching methods in this file — nothing
 * ever reads it back out.
 */
export function useCreateDiscountCouponMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (discountCoupon) => {
      const response = await httpClient.post(`${baseUrl}/api/v2/discount-coupon/`, discountCoupon, {
        headers: { 'Content-Type': 'application/json' },
      })
      return response.data
    },
  })
}

/**
 * Task 033 scope: mutations backing the 2 remaining specialized "add coupon"
 * screens (`app/discountCoupon/automaticDiscountCouponsAdd*`,
 * `ambassadorDiscountCouponsAdd*`) — distinct from the standard add screen
 * above (task 032) and from the combo screen
 * (`comboDiscountCouponsAddController.js`), which reuses
 * `useCreateDiscountCouponMutation` as-is: its `save()` calls the exact same
 * `DiscountCouponsService.save(discountCoupon)` / `/api/v2/discount-coupon/`
 * call as the standard screen (confirmed via `grep -n
 * "DiscountCouponsService\." app/discountCoupon/comboDiscountCouponsAddController.js`:
 * only `.save(discountCoupon)`), so `ComboDiscountCouponAddPage.jsx` calls
 * that existing hook directly rather than duplicating an identical mutation
 * under a new name.
 *
 * The automatic/ambassador variants below each hit their own distinct
 * endpoint, so each gets its own mutation, following this file's established
 * one-hook-per-legacy-service-method convention.
 *
 * Both follow the same "who created it" log pattern already used by
 * `useRemoveAutomaticCouponsMutation` above (`buildLogNamespace`/
 * `buildLogPayload` in the legacy controllers): a `POST /statusLogs/create`
 * call with `oldStatus: "BLANK"` and `newStatus` set to the JSON-stringified
 * payload just sent to the Astrea API. Reproduced here as
 * `buildDiscountCouponLogPayload`, reused by both mutations below.
 */
function buildDiscountCouponLogPayload(userLogin, couponsPayload) {
  const [namespace] = (userLogin || '').split('@')

  return {
    who: userLogin,
    namespace,
    oldStatus: 'BLANK',
    newStatus: JSON.stringify(couponsPayload),
  }
}

/**
 * Port of `DiscountCouponsService.saveAutomaticCoupons(discountCoupon)` +
 * `automaticDiscountCouponsAddController.js`'s `save()` request/log
 * sequencing:
 *
 *   DiscountCouponsService.saveAutomaticCoupons = function(discountCoupon) {
 *     var urlAstrea = discountCouponsService.getUrlAtrea();
 *     return $http({
 *       method: 'POST',
 *       url: urlAstrea + "/api/v2/dev/discount-coupon/automatic",
 *       headers: {'Content-Type': 'application/json'},
 *       data: discountCoupon
 *     }).then(function(data) { return data; });
 *   };
 *   ...
 *   $scope.save = function (discountCoupon) {
 *     ...
 *     var couponsPayload = buildAutomaticCouponsPayload(discountCoupon);
 *     DiscountCouponsService.saveAutomaticCoupons(couponsPayload).then(...);
 *     var logPayload = buildLogPayload(discountCoupon, couponsPayload);
 *     DiscountCouponsService.saveStatusLog(logPayload);
 *   };
 *
 * Same "fire-and-forget, not chained" ordering as
 * `useRemoveAutomaticCouponsMutation` above: the legacy code issues the log
 * request right after the save request, not inside its `.then`, and never
 * observes the log's own success/failure. Reproduced by awaiting both in
 * sequence inside this one `mutationFn` (network call order preserved) while
 * still resolving/rejecting based on the save request alone.
 *
 * Takes the already-built request payload (mirrors
 * `AutomaticDiscountCouponAddPage.jsx`'s own port of
 * `buildAutomaticCouponsPayload`, called before this mutation fires) since
 * that same shape is also what `newStatus` logs.
 */
export function useCreateAutomaticDiscountCouponMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (couponsPayload) => {
      const response = await httpClient.post('/discount-coupons/automatic', {
        payload: couponsPayload,
      })

      await saveStatusLog(buildDiscountCouponLogPayload(getUserLogin(), couponsPayload))

      return response.data
    },
  })
}

/**
 * Port of `DiscountCouponsService.saveAmbassadorCoupons(discountCoupon)` +
 * `ambassadorDiscountCouponsAddController.js`'s `save()`, same shape as
 * `useCreateAutomaticDiscountCouponMutation` above (POST + fire-and-forget
 * status log), hitting the ambassador-specific endpoint:
 *
 *   discountCouponsService.saveAmbassadorCoupons = function(discountCoupon) {
 *     var urlAstrea = discountCouponsService.getUrlAtrea();
 *     return $http({
 *       method: 'POST',
 *       url: urlAstrea + "/api/v2/dev/discount-coupon/automatic-marketing-partner",
 *       headers: {'Content-Type': 'application/json'},
 *       data: discountCoupon
 *     }).then(function(data) { return data; });
 *   };
 */
export function useCreateAmbassadorDiscountCouponMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (couponsPayload) => {
      const response = await httpClient.post('/discount-coupons/automatic-marketing-partner', {
        payload: couponsPayload,
      })

      await saveStatusLog(buildDiscountCouponLogPayload(getUserLogin(), couponsPayload))

      return response.data
    },
  })
}

export function useCreateAutomaticTurboDiscountCouponMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (couponsPayload) => {
      const response = await httpClient.post('/discount-coupons/automatic-additional-resource', {
        payload: couponsPayload,
      })

      saveStatusLog(buildDiscountCouponLogPayload(getUserLogin(), couponsPayload)).catch(() => {})

      return response.data
    },
  })
}

function buildAutomaticTurboCouponsLogPayload(userLogin, resourceTypes) {
  const [namespace] = (userLogin || '').split('@')
  const resourceTypesLabel =
    Array.isArray(resourceTypes) && resourceTypes.length > 0 ? resourceTypes.join(',') : 'TODOS'

  return {
    who: userLogin,
    namespace,
    oldStatus: 'BLANK',
    newStatus: `Automatic turbo coupons cleaned (resourceTypes: ${resourceTypesLabel}) at: ${new Date()}`,
  }
}

export function useRemoveAutomaticTurboCouponsMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (resourceTypes) => {
      const response = await httpClient.post(
        '/discount-coupons/automatic-additional-resource-clean-up',
        {
          resourceTypes,
        },
      )

      saveStatusLog(buildAutomaticTurboCouponsLogPayload(getUserLogin(), resourceTypes)).catch(
        () => {},
      )

      return response.data
    },
  })
}

export function useApplyCouponToTenantMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async ({ tenantId, couponCode }) => {
      const response = await httpClient.post('/discount-coupons/apply-coupon', {
        tenantId,
        couponCode,
      })

      await saveStatusLog({
        who: getUserLogin(),
        namespace: (getUserLogin() || '').split('@')[0],
        oldStatus: 'BLANK',
        newStatus: `Coupon ${couponCode} applied to tenant ${tenantId} at: ${new Date()}`,
      })

      return response.data
    },
  })
}

export function useMarketingPartnersQuery() {
  return useQuery({
    queryKey: ['marketingPartners'],
    queryFn: async () => {
      const response = await httpClient.post('/discount-coupons/marketing-partners')
      const partners = Array.isArray(response.data) ? response.data : []
      return [...partners].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'))
    },
  })
}

export function useDeleteMarketingPartnerMutation() {
  const { getUserLogin } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (partner) => {
      await httpClient.post('/discount-coupons/marketing-partners/delete', { id: partner.id })

      await saveStatusLog({
        who: getUserLogin(),
        namespace: (getUserLogin() || '').split('@')[0],
        oldStatus: 'BLANK',
        newStatus: `Marketing partner ${partner.id} (${partner.name}) deleted at: ${new Date()}`,
      })

      return partner
    },
    onSuccess: (partner) => {
      queryClient.setQueryData(['marketingPartners'], (current) =>
        Array.isArray(current) ? current.filter((existing) => existing.id !== partner.id) : current,
      )
    },
  })
}
